import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import {
  buildDashboardCharts,
  pipelineFromKpis,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import localApiAdapter from '../../core/services/localApiAdapter';
import { HR_EMPLOYEES_REFRESH_EVENT, useHrApiRefreshListener } from '../ressources-humaines/hrRefreshEvents';

const ABSENT_STATUSES = new Set(['AN', 'AJ', 'AI', 'ABSENT', 'ABSENCE']);

function isPresentEntry(entry) {
  if (!entry) return false;
  const status = String(entry.status || '').toUpperCase();
  return !ABSENT_STATUSES.has(status);
}

function computeDepartmentStats(employees, entries, naLabel) {
  const entryByEmp = {};
  (entries || []).forEach((e) => {
    if (e.employee_id) entryByEmp[e.employee_id] = e;
  });

  const deptMap = {};
  (employees || [])
    .filter((e) => ['active', 'actif'].includes(String(e.status || '').toLowerCase()))
    .forEach((emp) => {
      const dept = emp.affectation || emp.department || naLabel;
      if (!deptMap[dept]) deptMap[dept] = { present: 0, total: 0 };
      deptMap[dept].total += 1;
      if (isPresentEntry(entryByEmp[emp.id])) deptMap[dept].present += 1;
    });

  return Object.entries(deptMap)
    .map(([department, { present, total }]) => ({
      department,
      present,
      absent: total - present,
      rate: total > 0 ? Math.round((present / total) * 100) : 0,
    }))
    .sort((a, b) => b.rate - a.rate);
}

const DEFAULT_STATS = {
  totalEmployees: 0,
  presentToday: 0,
  absentToday: 0,
  lateToday: 0,
  onLeaveToday: 0,
  overtimeHoursMonth: 0,
  attendanceRate: 0,
  departmentStats: [],
};

export default function PointageAnalysesRapportsPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.pointage.pages.pointageAnalysesRapportsPage.${key}`, opts);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(DEFAULT_STATS);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const month = today.getMonth() + 1;
      const year = today.getFullYear();

      const [dash, dailyStats, dailyEntries, overtimeStats, employees] = await Promise.all([
        localApiAdapter.get('/timetracking/dashboard').catch(() => ({})),
        localApiAdapter.get('/timetracking/daily/stats', { date: todayStr }).catch(() => ({})),
        localApiAdapter.get('/timetracking/daily', { date: todayStr }).catch(() => []),
        localApiAdapter.get('/timetracking/overtime/stats', { month, year }).catch(() => ({})),
        localApiAdapter.get('/hr/employees').catch(() => []),
      ]);

      const kpis = dash.kpis || {};
      const presentToday = Number(dailyStats.present ?? kpis.presentToday) || 0;
      const absentToday = Number(dailyStats.absent ?? kpis.absentees) || 0;
      const lateToday = Number(dailyStats.late ?? kpis.lateArrivals) || 0;
      const overtimeHoursMonth = Number(overtimeStats.totalHours ?? kpis.overtimeHours) || 0;
      const tracked = presentToday + absentToday;
      const attendanceRate = tracked > 0 ? Math.round((presentToday / tracked) * 100) : 0;
      const empRows = Array.isArray(employees) ? employees : [];
      const entryRows = Array.isArray(dailyEntries) ? dailyEntries : [];
      const departmentStats = computeDepartmentStats(empRows, entryRows, p('na'));

      setData({
        totalEmployees: empRows.filter((e) =>
          ['active', 'actif'].includes(String(e.status || '').toLowerCase()),
        ).length || tracked,
        presentToday,
        absentToday,
        lateToday,
        onLeaveToday: 0,
        overtimeHoursMonth,
        attendanceRate,
        departmentStats,
      });
    } catch (error) {
      console.error('[PointageAnalyses] load error:', error);
      setData(DEFAULT_STATS);
    } finally {
      setLoading(false);
    }
  }, [p]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useHrApiRefreshListener(HR_EMPLOYEES_REFRESH_EVENT, loadData);

  const stats = useMemo(
    () => [
      { label: p('stat_total_employees'), value: data.totalEmployees, icon: Users, variant: 'forest' },
      { label: p('stat_present'), value: data.presentToday, icon: CheckCircle, variant: 'copper' },
      { label: p('stat_absent'), value: data.absentToday, icon: XCircle, variant: 'forest' },
      {
        label: p('stat_attendance_rate'),
        value: `${Math.round(Number(data.attendanceRate) || 0)}%`,
        icon: TrendingUp,
        variant: 'copper',
      },
    ],
    [data, p],
  );

  const insights = useMemo(
    () => [
      {
        label: p('insight_attendance_rate'),
        value: `${Math.round(Number(data.attendanceRate) || 0)}%`,
        hint: p('insight_attendance_rate_hint', {
          present: data.presentToday,
          total: data.presentToday + data.absentToday,
        }),
      },
      {
        label: p('insight_overtime_hours'),
        value: `${Number(data.overtimeHoursMonth || 0).toFixed(1)} ${p('hours_unit')}`,
        hint: p('insight_overtime_hours_hint'),
      },
      {
        label: p('insight_late_count'),
        value: String(data.lateToday),
        hint: p('insight_late_count_hint'),
      },
    ],
    [data, p],
  );

  const charts = useMemo(() => {
    const deptRows = (data.departmentStats || []).slice(0, 8).map((row) => ({
      label: row.department || p('na'),
      value: row.rate ?? 0,
      display: `${Math.round(row.rate ?? 0)}%`,
    }));

    const onTime = Math.max(0, data.presentToday - data.lateToday);

    return buildDashboardCharts({
      ranked: {
        title: p('chart_dept_ranked'),
        subtitle: p('chart_dept_ranked_subtitle'),
        alwaysShow: true,
        rows: deptRows.length ? deptRows : [{ label: p('na'), value: 0, display: '0%' }],
      },
      status: {
        title: p('chart_status_title'),
        subtitle: p('chart_status_subtitle'),
        active: data.presentToday,
        inactive: data.absentToday,
        alwaysShow: true,
      },
      pipeline: {
        title: p('chart_pipeline_title'),
        subtitle: p('chart_pipeline_subtitle'),
        alwaysShow: true,
        rows: pipelineFromKpis([
          { label: p('pipeline_on_time'), value: onTime },
          { label: p('pipeline_late'), value: data.lateToday },
          {
            label: p('pipeline_overtime'),
            value: Math.round(Number(data.overtimeHoursMonth) || 0),
          },
        ]),
      },
    });
  }, [data, p]);

  const table = useMemo(() => {
    const rows = (data.departmentStats || []).map((row) => ({
      department: row.department || p('na'),
      present: row.present ?? '—',
      absent: row.absent ?? '—',
      rate: row.rate != null ? `${Math.round(row.rate)}%` : '—',
    }));
    if (!rows.length) {
      return {
        title: p('table_summary'),
        columns: [
          { key: 'metric', label: p('col_metric') },
          { key: 'value', label: p('col_value') },
        ],
        rows: [
          { metric: p('stat_total_employees'), value: data.totalEmployees },
          { metric: p('stat_present'), value: data.presentToday },
          { metric: p('stat_absent'), value: data.absentToday },
          { metric: p('stat_late'), value: data.lateToday },
          {
            metric: p('stat_overtime_hours'),
            value: `${Number(data.overtimeHoursMonth || 0).toFixed(1)} ${p('hours_unit')}`,
          },
          {
            metric: p('stat_attendance_rate'),
            value: `${Math.round(Number(data.attendanceRate) || 0)}%`,
          },
        ],
      };
    }
    return {
      title: p('table_by_department'),
      columns: [
        { key: 'department', label: p('col_department') },
        { key: 'present', label: p('col_present') },
        { key: 'absent', label: p('col_absent') },
        { key: 'rate', label: p('col_rate') },
      ],
      rows,
    };
  }, [data, p]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={loadData}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="pointage_analyses"
      emptyLabel={p('empty')}
    />
  );
}
